import { filialMargeProzent, type Preiskette } from "@repo/shared";

import { euro } from "../../lib/regeln";

const prozent = (wert: number) => `${wert.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;
const betrag = (cents: number | null) => (cents === null ? "—" : euro(cents));

/**
 * Preis-Card der Konfiguratoren mit Herstellerpreis: UVP, Rabatt und EK des Herstellers,
 * darunter die Preise für die Filiale – je mit dem Gewinn vor Steuer auf die UVP des
 * Herstellers inkl. Teuerungszuschlag (netto). Ein Stoffanteil (Raffrollo) steht als
 * eigener Block darunter; die Blöcke darüber gelten dann nur für die Technik.
 */
export function PreisUebersicht({ preis }: { preis: Preiskette }) {
  const produkt = preis.anteile.produkt;
  const stoff = preis.anteile.stoff;
  const hp = produkt.herstellerpreis;
  if (!hp) return null;

  const teuerung = hp.herstellerUvpMitTeuerungBruttoCents !== undefined;
  // Grundlage der Gewinne: UVP inkl. Teuerung ÷ (1 + USt) – ohne bestimmbare UVP keiner.
  const uvpNetto = hp.herstellerUvpMitTeuerungNettoCents ?? hp.herstellerUvpNettoCents;
  const gewinn = (ekNettoCents: number) => (uvpNetto === null ? null : uvpNetto - ekNettoCents);
  const rabattFiliale = filialMargeProzent({
    preisart: hp.preisart,
    rabattProzent: hp.rabattProzent,
    aufschlagProzent: produkt.aufschlagProzent,
  });

  return (
    <>
      {stoff && <p className="muted small preiskette__hinweis">Technik ohne Stoff</p>}
      <dl className="preiskette">
        <dt className="fett">Hersteller Brutto (UVP)</dt>
        <dd className="fett">{betrag(hp.herstellerUvpBruttoCents)}</dd>
        {teuerung && (
          <>
            <dt>Hersteller Brutto (UVP inkl. Teuerungszuschlag {prozent(hp.teuerungProzent ?? 0)})</dt>
            <dd>{betrag(hp.herstellerUvpMitTeuerungBruttoCents ?? null)}</dd>
          </>
        )}
        <dt>Herstellerrabatt</dt>
        <dd>
          {prozent(hp.rabattProzent)}
          {hp.rabattDynamisch ? " (dynamisch)" : ""}
        </dd>
        <dt className="fett">Hersteller EK netto</dt>
        <dd className="fett">{euro(produkt.ekNettoCents)}</dd>
        <dt className="trenner">Gewinn vor Steuer</dt>
        <dd className="trenner">{betrag(gewinn(produkt.ekNettoCents))}</dd>
      </dl>

      <h3 className="preiskette__titel">Preise für die Filiale</h3>
      <dl className="preiskette">
        <dt>Filiale EK netto</dt>
        <dd>{euro(produkt.filialEkNettoCents)}</dd>
        <dt>Rabatt auf Hersteller-UVP</dt>
        <dd>{rabattFiliale === null ? "—" : prozent(rabattFiliale)}</dd>
        <dt className="trenner">Gewinn vor Steuer</dt>
        <dd className="trenner">{betrag(gewinn(produkt.filialEkNettoCents))}</dd>
      </dl>

      {stoff && (
        <>
          <h3 className="preiskette__titel">Stoffanteil</h3>
          <dl className="preiskette">
            <dt>Stoff-EK netto</dt>
            <dd>{euro(stoff.ekNettoCents)}</dd>
            <dt>Filiale EK netto</dt>
            <dd>{euro(stoff.filialEkNettoCents)}</dd>
            <dt>UVP netto (Stoffliste)</dt>
            <dd>{euro(stoff.uvpNettoCents)}</dd>
          </dl>
        </>
      )}
    </>
  );
}
